/**
 * The scene stack and the frame loop (docs/spec/07 §1): input goes to the top scene, the top scene
 * (and anything it overlays) is drawn into the virtual screen, and the display pass puts that on
 * the window.
 */

import type { Controls } from '../input/controls';
import type { Display } from '../render/display/display';
import type { Screen } from '../render/screen';
import type { PointerInput, Route, Scene, SceneFactory, SceneHost, Services } from '../ui/scene';

export class App implements SceneHost {
  private stack: { scene: Scene; route: Route }[] = [];
  private last = 0;
  /** Tests: the animation-frame loop stops advancing time; only `frame()` calls move it (window.__game.advance). */
  frozen = false;
  /** Something changed since the last draw (frozen mode draws only then, so screenshots are quick). */
  private dirty = true;

  constructor(
    readonly services: Services,
    private readonly factory: SceneFactory,
    private readonly screen: Screen,
    private readonly display: Display,
    private readonly controls: Controls,
  ) {}

  get top(): Scene | null {
    return this.stack[this.stack.length - 1]?.scene ?? null;
  }

  get route(): Route | null {
    return this.stack[this.stack.length - 1]?.route ?? null;
  }

  go(route: Route): void {
    for (const { scene } of [...this.stack].reverse()) scene.leave?.();
    this.stack = [];
    this.open(route);
  }

  push(route: Route): void {
    this.top?.leave?.();
    this.open(route);
  }

  back(): void {
    const gone = this.stack.pop();
    gone?.scene.leave?.();
    const top = this.top;
    if (top) {
      this.controls.latch.reset();
      top.enter?.();
    } else this.go({ to: 'title', skipPress: true });
  }

  private open(route: Route): void {
    this.dirty = true;
    const scene = this.factory(route, this);
    this.stack.push({ scene, route });
    this.controls.latch.reset();
    scene.enter?.();
  }

  pointer(p: PointerInput): void {
    this.top?.pointer?.(p);
  }

  /** Start the loop. */
  run(): void {
    const frame = (now: number): void => {
      const dt = this.last === 0 ? 16 : Math.min(100, now - this.last);
      this.last = now;
      if (!this.frozen) this.frame(dt);
      else if (this.dirty) this.render();
      requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  }

  frame(dtMs: number): void {
    this.controls.update(dtMs);
    this.top?.update(dtMs);
    this.dirty = true;
    this.render();
  }

  render(): void {
    this.dirty = false;
    if (this.stack.length === 0) return;
    let base = this.stack.length - 1;
    while (base > 0 && this.stack[base]?.scene.overlay === true) base--;
    const size = this.stack[base]?.scene.size?.() ?? { w: 320, h: 200 };
    this.screen.resize(size.w, size.h);
    for (let i = base; i < this.stack.length; i++) this.stack[i]?.scene.draw(this.screen);
    const o = this.services.options.get();
    this.display.resize();
    this.display.present(this.screen.canvas, o.crt, o.scale);
  }
}
